import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Award,
  Upload,
  X,
  Eye,
  Download,
  FileText,
  Save,
  AlertCircle,
} from "lucide-react";
import { useAccreditionsProfileData } from "@/lib/profile/accreditionsProfileData";
import { DocumentViewerDialog } from "../../../src/lib/profile/DocumentViewerDialog";
import { useDocumentsData } from "../Documents/documentsData";
import { useToast } from "@/hooks/use-toast";
import React from "react";

export default function AccreditionsProfileTab() {
  const {
    isDarkMode,
    documentTypes,
    accreditations,
    updateAccreditation,
    handleFileUpload,
    isExpired,
    isExpiringWithin30Days,
    removeAccreditation,
    addUserAccreditationData,
    isCurrentProfileLoading,
    handleFileDownload,
    handleFileView,
    isViewerOpen,
    viewerDocument,
    setIsViewerOpen,
  } = useAccreditionsProfileData();

  const { documentSizeLimitInMbs } = useDocumentsData() || {};

  const { toast } = useToast();
  const [fileError, setFileError] = React.useState<string>("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Maximum number of documents allowed
  const MAX_DOCUMENTS = 5;
  const currentDocumentCount = accreditations?.length || 0;
  const canUploadMore = currentDocumentCount < MAX_DOCUMENTS;

  // Helper function to validate file size
  const validateFileSize = (file: File): boolean => {
    const maxSizeInBytes = documentSizeLimitInMbs * 1024 * 1024;
    return file.size <= maxSizeInBytes;
  };

  // Helper function to format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  // Validate file type
  const validateFileType = (file: File): boolean => {
    const validExtensions = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png"];
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    return validExtensions.includes(fileExtension);
  };

  // Get file validation error message
  const getFileValidationError = (file: File): string => {
    if (!validateFileType(file)) {
      return "Invalid file type. Please select a PDF, DOC, DOCX, JPG, or PNG file.";
    }

    if (!validateFileSize(file)) {
      return `File size (${formatFileSize(
        file.size
      )}) exceeds ${documentSizeLimitInMbs}MB limit.`;
    }

    return "";
  };

  // Handle file upload with validation
  const handleFileUploadWithValidation = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    setFileError("");

    // Check if user has reached the maximum document limit
    if (currentDocumentCount >= MAX_DOCUMENTS) {
      toast({
        title: "Maximum Documents Reached",
        description: `You can only upload up to ${MAX_DOCUMENTS} documents. Please remove some documents before adding new ones.`,
        variant: "destructive",
      });

      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Validate file
    const error = getFileValidationError(file);
    if (error) {
      setFileError(error);
      toast({
        title: "File Validation Error",
        description: error,
        variant: "destructive",
      });

      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // If validation passes, call the original handleFileUpload
    setFileError("");
    handleFileUpload(event);

    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    try {
      await addUserAccreditationData();
    } catch (error) {
      console.error("Error saving accreditations:", error);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3
              className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Accreditations & Skills
            </h3>
            <div
              className={`text-xs mt-1 transition-colors duration-300 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {currentDocumentCount} of {MAX_DOCUMENTS} documents uploaded
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              disabled={!canUploadMore}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
            <p
              className={`text-xs ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Max file size: {documentSizeLimitInMbs}MB • Max {MAX_DOCUMENTS}{" "}
              documents
            </p>
          </div>

          <input
            ref={fileInputRef}
            id="accreditation-upload"
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            className="hidden"
            onChange={handleFileUploadWithValidation}
          />
        </div>
        {fileError && (
          <div
            className={`flex items-start gap-3 rounded-lg border px-4 py-3 mb-4 transition-colors ${
              isDarkMode
                ? "bg-yellow-900/20 border-yellow-800 text-yellow-300"
                : "bg-yellow-50 border-yellow-200 text-yellow-800"
            }`}
          >
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
            <p className="text-sm font-medium">{fileError}</p>
          </div>
        )}

        {!canUploadMore && (
          <div
            className={`mb-4 p-3 rounded-lg border ${
              isDarkMode
                ? "bg-yellow-900/20 border-yellow-800 text-yellow-300"
                : "bg-yellow-50 border-yellow-200 text-yellow-800"
            }`}
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">
                Maximum documents reached ({MAX_DOCUMENTS}/{MAX_DOCUMENTS})
              </span>
            </div>
            <p className="text-xs mt-1">
              Remove some documents before adding new ones.
            </p>
          </div>
        )}

        {accreditations?.length === 0 ? (
          <div
            className={`text-center py-8 border-2 border-dashed rounded-lg transition-colors duration-300 ${
              isDarkMode ? "border-gray-600" : "border-gray-200"
            }`}
          >
            <Award
              className={`w-12 h-12 mx-auto mb-4 transition-colors duration-300 ${
                isDarkMode ? "text-gray-500" : "text-gray-400"
              }`}
            />
            <h4
              className={`text-lg font-medium mb-2 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              No accreditations uploaded
            </h4>
            <p
              className={`mb-4 transition-colors duration-300 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Upload your professional certificates and training documents
            </p>
            <div
              className={`text-xs ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Supported: PDF, DOC, DOCX, JPG, PNG (max {documentSizeLimitInMbs}
              MB) • Max {MAX_DOCUMENTS} documents
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {accreditations?.map((accreditation) => (
              <div
                key={accreditation.id}
                className={`border rounded-lg p-4 transition-colors duration-300 ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                        isDarkMode ? "bg-blue-900/30" : "bg-blue-100"
                      }`}
                    >
                      <FileText
                        className={`w-5 h-5 transition-colors duration-300 ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}
                      />
                    </div>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label
                          className={`text-xs transition-colors duration-300 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          Document Name
                        </Label>
                        <Input
                          value={accreditation.name}
                          onChange={(e) =>
                            updateAccreditation(
                              accreditation.id,
                              "name",
                              e.target.value
                            )
                          }
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label
                          className={`text-xs transition-colors duration-300 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          Type
                        </Label>
                        <select
                          value={accreditation.type}
                          onChange={(e) =>
                            updateAccreditation(
                              accreditation.id,
                              "type",
                              e.target.value
                            )
                          }
                          className={`mt-1 w-full h-10 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-300 ${
                            isDarkMode
                              ? "border-gray-600 bg-gray-700 text-white"
                              : "border-gray-300 bg-white text-gray-900"
                          }`}
                        >
                          {documentTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex-1">
                          <Label
                            className={`text-xs transition-colors duration-300 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            Issue Date
                          </Label>
                          <Input
                            type="date"
                            value={accreditation.issueDate}
                            onChange={(e) =>
                              updateAccreditation(
                                accreditation.id,
                                "issueDate",
                                e.target.value
                              )
                            }
                            className="mt-1"
                          />
                        </div>
                        <div className="flex-1">
                          <Label
                            className={`text-xs transition-colors duration-300 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            Expiry Date
                          </Label>
                          <Input
                            type="date"
                            value={accreditation.expiryDate}
                            onChange={(e) =>
                              updateAccreditation(
                                accreditation.id,
                                "expiryDate",
                                e.target.value
                              )
                            }
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {accreditation.expiryDate && (
                      <div className="text-right">
                        {isExpired(accreditation.expiryDate) ? (
                          <Badge className="bg-red-100 text-red-700">
                            Expired
                          </Badge>
                        ) : isExpiringWithin30Days(accreditation.expiryDate) ? (
                          <Badge className="bg-yellow-100 text-yellow-700">
                            Expiring Soon
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">
                            Valid
                          </Badge>
                        )}
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleFileView(accreditation)}
                      className="text-blue-500 hover:text-blue-700"
                      title="View File"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>

                    <Button
                      onClick={() => handleFileDownload(accreditation)}
                      variant="ghost"
                      size="sm"
                    >
                      <Download className="w-4 h-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAccreditation(accreditation.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div
                  className={`mt-2 flex items-center gap-4 text-xs transition-colors duration-300 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  <span>{accreditation.fileName}</span>
                  <span>{accreditation.fileSize}</span>
                </div>
              </div>
            ))}

            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
              <div
                className={`text-xs ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Supported file types: PDF, DOC, DOCX, JPG, PNG • Max size:{" "}
                {documentSizeLimitInMbs}MB • Max {MAX_DOCUMENTS} documents (
                {currentDocumentCount}/{MAX_DOCUMENTS})
              </div>
              <Button
                onClick={handleSave}
                disabled={isCurrentProfileLoading}
                className={`bg-slate-900 hover:bg-slate-800 text-white ${
                  isCurrentProfileLoading
                    ? "opacity-50 pointer-events-none"
                    : ""
                }`}
              >
                <Save className="w-4 h-4 mr-2" />
                {isCurrentProfileLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        )}
      </div>
      <DocumentViewerDialog
        document={viewerDocument}
        open={isViewerOpen}
        onOpenChange={setIsViewerOpen}
        isDarkMode={isDarkMode}
      />
    </div>
  );
}
